import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { auth, db, storage } from "../firebase";
import {
  doc,
  getDoc,
  collection,
  addDoc,
  query,
  orderBy,
  onSnapshot,
  serverTimestamp,
  deleteDoc,
  updateDoc,
} from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import "./CozyNook.css";

function CozyNook() {
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [posts, setPosts] = useState([]);
  const [newPost, setNewPost] = useState("");
  const [postPhotos, setPostPhotos] = useState([]);
  const [activeTab, setActiveTab] = useState("daily");
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [editingPostId, setEditingPostId] = useState(null);
  const [editedText, setEditedText] = useState("");
  const [menuOpen, setMenuOpen] = useState(null);
  const [showOptions, setShowOptions] = useState(false);

  const today = new Date();
  const isToday = (d) =>
    d.getDate() === today.getDate() &&
    d.getMonth() === today.getMonth() &&
    d.getFullYear() === today.getFullYear();

  useEffect(() => {
    const fetchData = async () => {
      const user = auth.currentUser;
      if (!user) {
        navigate("/", { replace: true });
        return;
      }

      try {
        const docRef = doc(db, "users", user.uid);
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) setProfile(docSnap.data());

        const postsRef = collection(db, "users", user.uid, "posts");
        const q = query(postsRef, orderBy("timestamp", "desc"));
        const unsubscribe = onSnapshot(q, (snapshot) => {
          const postList = snapshot.docs.map((doc) => ({
            id: doc.id,
            ...doc.data(),
          }));
          setPosts(postList);
        });

        return unsubscribe;
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [navigate]);

  const handlePhotoChange = (e) => {
    const files = Array.from(e.target.files);
    setPostPhotos(files);
  };

  const handlePostSubmit = async () => {
    const user = auth.currentUser;
    if (!newPost.trim() && postPhotos.length === 0) return alert("Add text or photo!");
    if (!user) return;

    try {
      const photoURLs = [];
      for (const file of postPhotos) {
        const photoRef = ref(storage, `posts/${user.uid}/${Date.now()}_${file.name}`);
        await uploadBytes(photoRef, file);
        const url = await getDownloadURL(photoRef);
        photoURLs.push(url);
      }

      await addDoc(collection(db, "users", user.uid, "posts"), {
        text: newPost.trim(),
        photoURLs,
        timestamp: serverTimestamp(),
      });

      setNewPost("");
      setPostPhotos([]);
    } catch (err) {
      console.error(err);
      alert("Failed to post.");
    }
  };

  const handleDeletePost = async (postId) => {
    if (!window.confirm("Delete this post?")) return;
    const user = auth.currentUser;
    try {
      await deleteDoc(doc(db, "users", user.uid, "posts", postId));
    } catch (err) {
      console.error(err);
    }
  };

  const handleEditPost = async (postId) => {
    const user = auth.currentUser;
    if (!editedText.trim()) return;
    try {
      await updateDoc(doc(db, "users", user.uid, "posts", postId), {
        text: editedText.trim(),
      });
      setEditingPostId(null);
      setEditedText("");
    } catch (err) {
      console.error(err);
    }
  };

  const isSameDay = (d1, d2) =>
    d1.getDate() === d2.getDate() &&
    d1.getMonth() === d2.getMonth() &&
    d1.getFullYear() === d2.getFullYear();

  const filteredPosts = posts.filter((p) => {
    if (!p.timestamp?.toDate) return false;
    const postDate = p.timestamp.toDate();
    if (activeTab === "daily") return isSameDay(postDate, selectedDate);
    if (activeTab === "monthly")
      return (
        postDate.getMonth() === selectedDate.getMonth() &&
        postDate.getFullYear() === selectedDate.getFullYear()
      );
    if (activeTab === "yearly") return postDate.getFullYear() === selectedDate.getFullYear();
    return false;
  });

  const handlePrevDay = () =>
    setSelectedDate(new Date(selectedDate.setDate(selectedDate.getDate() - 1)));
  const handleNextDay = () =>
    setSelectedDate(new Date(selectedDate.setDate(selectedDate.getDate() + 1)));

  const handleSelectDay = (day) => {
    const newDate = new Date(selectedDate);
    newDate.setDate(day);
    setSelectedDate(newDate);
    setActiveTab("daily");
  };

  const postsByDay = posts.reduce((acc, p) => {
    const postDate = p.timestamp?.toDate();
    if (!postDate) return acc;
    const key = `${postDate.getFullYear()}-${postDate.getMonth()}-${postDate.getDate()}`;
    if (!acc[key]) acc[key] = [];
    acc[key].push(p);
    return acc;
  }, {});

  if (loading) return <div className="loading-screen">Loading...</div>;

  return (
    <div className="nook-page">
      {/* Sidebar */}
      <aside className="nook-sidebar">
        <div>
          <div className="profile-card">
            <img
              src={profile?.photoURL || "https://via.placeholder.com/100"}
              alt="Profile"
              className="profile-pic"
            />
            <h2>{profile?.name}</h2>
            <p>@{profile?.username}</p>
          </div>

          <nav className="sidebar-menu">
            <button className="sidebar-btn" onClick={() => setActiveTab("daily")}>
              🏠 Diari
            </button>
            <button className="sidebar-btn" onClick={() => navigate("/nook/map")}>
              🗺️ Map
            </button>
            <button className="sidebar-btn" onClick={() => navigate("/nook/finances")}>
              💰 Finances
            </button>
            <button className="sidebar-btn" onClick={() => navigate("/profile-setup")}>
              ✏️ Edit Profile
            </button>
            <button className="sidebar-btn">🏆 Achievements</button>
            <button
              className="sidebar-btn"
              onClick={() => navigate("/openspace")}
            >
              🌐 Open Space
            </button>
            <button className="sidebar-btn" onClick={() => navigate("/admin")}>
              🏠 Admin
            </button>
          </nav>
        </div>
      </aside>
      {/* Floating Border */}
      <div className="nook-floating-border"></div>
      
      {/* Floating "What happened?" button */}
      <button
        className="what-happened-btn"
        onClick={() => setShowOptions(!showOptions)}
      >
        What happened?
      </button>

      {/* Popup menu */}
      {showOptions && (
        <div className="post-options-popup">
          <button>Mood Tracker</button>
          <button>Upload Photos</button>
          <button>Write Your Story</button>
          <button>Share a Poem</button>
          <button>Share a Music Lyric</button>
          <button>Share a Music Clip</button>
        </div>
      )}

      {/* Main */}
      <main className="nook-main">  
      {/* Tabs + Date in one horizontal line */}
      <div className="tabs-row">
        <div className="tabs">
          {["daily", "monthly", "yearly"].map((tab) => (
            <button
              key={tab}
              className={`tab ${activeTab === tab ? "active" : ""}`}
              onClick={() => setActiveTab(tab)}
            >
              {tab.charAt(0).toUpperCase() + tab.slice(1)}
            </button>
          ))}
        </div>

        {/* Date (page number) */}
        <div className="page-date">
          {selectedDate.toLocaleDateString("en-US", {
            month: "short",
            day: "numeric",
            year: "numeric",
            })}
        </div>
      </div>

        {/* DAILY */}
        {activeTab === "daily" && (
          <div className="daily-view">
            <div className="day-header">
              <button onClick={handlePrevDay} className="arrow-btn">
                🡰
              </button>
              <button onClick={handleNextDay} className="arrow-btn">
                🡲
              </button>
            </div>

            {/* Timeline */}
            <div className="timeline">
              {filteredPosts.length === 0 ? (
                <p>No posts for this day ☕</p>
              ) : (
                filteredPosts.map((p) => (
                  <div className="nook-post" key={p.id}>
                    <div className="post-header">
                      <span>{p.timestamp?.toDate?.().toLocaleTimeString()}</span>
                      <div
                        className="menu-icon"
                        onClick={() => setMenuOpen(menuOpen === p.id ? null : p.id)}
                      >
                        ⋮
                      </div>
                      {menuOpen === p.id && (
                        <div className="menu-dropdown">
                          <button
                            onClick={() => {
                              setEditingPostId(p.id);
                              setEditedText(p.text);
                            }}
                          >
                            Edit
                          </button>
                          <button onClick={() => handleDeletePost(p.id)}>Delete</button>
                        </div>
                      )}
                    </div>
                    {editingPostId === p.id ? (
                      <div>
                        <textarea
                          value={editedText}
                          onChange={(e) => setEditedText(e.target.value)}
                        />
                        <button onClick={() => handleEditPost(p.id)}>Save</button>
                      </div>
                    ) : (
                      <>
                        <p>{p.text}</p>
                        {p.photoURLs && p.photoURLs.length > 0 && (
                          <div className="post-photos">
                            {p.photoURLs.map((url, i) => (
                              <img key={i} src={url} alt={`Post ${i}`} className="post-photo" />
                            ))}
                          </div>
                        )}
                      </>
                    )}
                  </div>
                ))
              )}
            </div>
          </div>
        )}

        {/* MONTHLY */}
        {activeTab === "monthly" && (
          <div className="month-grid">
            {Array.from({ length: 31 }).map((_, i) => {
              const dayKey = `${selectedDate.getFullYear()}-${selectedDate.getMonth()}-${
                i + 1
              }`;
              const hasPost = postsByDay[dayKey];
              return (
                <div
                  key={i}
                  className={`day-box ${hasPost ? "has-post" : ""}`}
                  onClick={() => handleSelectDay(i + 1)}
                >
                  <span className="day-number">{i + 1}</span>
                </div>
              );
            })}
          </div>
        )}

        {/* YEARLY */}
        {activeTab === "yearly" && (
          <div className="year-timeline">
            {Object.entries(
              posts.reduce((acc, p) => {
                const year = p.timestamp?.toDate()?.getFullYear();
                if (!acc[year]) acc[year] = [];
                acc[year].push(p);
                return acc;
              }, {})
            ).map(([year, yearPosts]) => (
              <div key={year} className="year-section">
                <div className="year-dot" />
                <div>
                  <h3>{year}</h3>
                  {yearPosts.map((p) => (
                    <p
                      key={p.id}
                      onClick={() => {
                        const postDate = p.timestamp?.toDate();
                        if (postDate) setSelectedDate(postDate);
                        setActiveTab("daily");
                      }}
                    >
                      • {p.text}
                    </p>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}

export default CozyNook;
